import { useEffect, useState } from "react";
import { Card, Col, Row, Form, Button } from "antd";
import { useForm } from "react-hook-form";
import {
  InputField,
  InputNumberField,
  SelectField,
} from "../../../../../atoms/FormFields";
import { useLocation, useNavigate } from "react-router-dom";
import FullPageLayout from "../../../../../templates/FullPageLayout";
import { updateMaterialById } from "../../../ducks/services";
import { AxiosError } from "axios";
import SuccessMessage from "../../../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../../../atoms/Toasts/ErrorMessage";
import { CreateMaterialType } from "../../../ducks/types";
import { useAppDispatch, useAppSelector } from "../../../../../config/hooks";
import {
  getMaterialDetailById,
  removeMaterialDetailData,
} from "../../../ducks/actions";
import { SelectFieldTags } from "../../../../../atoms/FormFields/SelectFieldTags";
import { getCompanyFieldsById } from "../../../../CompanySettings/TagsCategories/ducks/actions";
import { getUnitsFormData } from "../../../../CompanySettings/TakeoffMeasurements/ducks/actions";
import { NeedHelp } from "../../../../../atoms/CustomIcons";

interface CategoryType {
  label: string;
  value: string;
  color: string;
}

export default function EditMaterials() {
  const [loading, setLoading] = useState(false);
  const [categoryDropDown, setCategoryDropDown] = useState<CategoryType[]>([]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[2];
  const id = url?.pathname.split("/")[3];
  const materialDetailData = useAppSelector(
    (state) => state.inventory?.materialDetailData
  );
  const tagItems = useAppSelector((state) => state?.tags?.tagData);
  const unitDropdown = useAppSelector((state) => state?.takeOff?.unitFormData);

  useEffect(() => {
    if (tagItems?.data && tagItems?.data?.length > 0) {
      const dummyArray: CategoryType[] = [];
      tagItems?.data?.map((item: { name: string; id: string; color: string }) =>
        dummyArray.push({
          label: item?.name,
          value: item?.id,
          color: item?.color,
        })
      );
      setCategoryDropDown(dummyArray);
    } else {
      setCategoryDropDown([]);
    }
  }, [tagItems]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<CreateMaterialType>();

  const onSubmit = (val: CreateMaterialType) => {
    setLoading(true);

    const payload = {
      name: val?.name?.trim(),
      unit_id: val?.unit,
      cost: val?.cost ?? 0,
      category_ids:
        val?.category_ids && val?.category_ids?.length > 0
          ? val?.category_ids
          : [],
      company_id: companyId,
    };

    updateMaterialById(id, payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Material updated");
        navigate(`/inventory/materials/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (id) {
      dispatch(getMaterialDetailById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(removeMaterialDetailData());
  }, [dispatch]);

  useEffect(() => {
    if (materialDetailData?.data) {
      setValue("name", materialDetailData?.data?.name);
      setValue("unit", materialDetailData?.data?.unit_id);
      setValue("cost", materialDetailData?.data?.cost);
      setValue("category_ids", materialDetailData?.data?.category_ids);
    }
  }, [materialDetailData?.data, setValue]);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "MATERIALS",
      };

      dispatch(getCompanyFieldsById(payload));
      dispatch(getUnitsFormData(companyId));
    }
  }, [dispatch, companyId]);

  return (
    <FullPageLayout loading={loading || materialDetailData?.loader}>
      <Button className="btn-sm fixedTopBtn">
        <NeedHelp /> Need help?
      </Button>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col style={{ width: "672px", paddingBottom: 90 }}>
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2> Edit material </h2>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24} className="createForm">
                <h4> Material details </h4>
                <Row gutter={12}>
                  <Col span={12}>
                    <InputField
                      fieldName="name"
                      label="Material name"
                      control={control}
                      defaultValue=""
                      iProps={{
                        placeholder: "Enter material name",
                      }}
                      isRequired
                      rules={{
                        required: "Material name is required.",
                      }}
                      validate={errors.name && "error"}
                      validMessage={errors.name && errors.name.message}
                    />
                  </Col>
                  <Col span={6}>
                    <SelectField
                      fieldName="unit"
                      label="Unit"
                      control={control}
                      selectOption={unitDropdown}
                      iProps={{
                        placeholder: "Select unit",
                      }}
                      isRequired
                      rules={{
                        required: "Unit is required.",
                      }}
                      validate={errors.unit && "error"}
                      validMessage={errors.unit && errors.unit.message}
                      tooltipTitle="The unit (e.g., pieces, feet) used to measure and track material."
                    />
                  </Col>
                  <Col span={6}>
                    <InputNumberField
                      fieldName="cost"
                      label="Unit cost"
                      control={control}
                      iProps={{
                        placeholder: "0.00",
                        prefix: "$",
                      }}
                      isRequired
                      rules={{
                        required: "Unit cost is required.",
                      }}
                      validate={errors.cost && "error"}
                      validMessage={errors.cost && errors.cost.message}
                      tooltipTitle="The material's cost to you. This cost goes into the estimates."
                    />
                  </Col>
                </Row>

                <SelectFieldTags
                  fieldName="category_ids"
                  label="Categories"
                  control={control}
                  selectOption={categoryDropDown}
                  className="bottom_0"
                  iProps={{
                    placeholder: "Select categories",
                    mode: "multiple",
                  }}
                  // isRequired
                  // rules={{
                  //   required: "Categories is required.",
                  // }}
                  // validate={errors.category_ids && "error"}
                  // validMessage={
                  //   errors.category_ids && errors.category_ids.message
                  // }
                  tooltipTitle="Categories are used for combining materials into groups."
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate(`/inventory/materials/${companyId}`)}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  disabled={!isValid || !isDirty}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
